/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { theme } from '../../styles/theme'
import { useState, useEffect } from 'react'
import { useCalculator } from '../../hooks/useCalculator'
import { CalculatorChart } from '../Calculator/CalculatorChart'
import { CalculatorTable } from '../Calculator/CalculatorTable'
import Cleave from 'cleave.js/react'

interface CalculatorModalProps {
  isOpen: boolean
  strategyName: string
  cagr: number
  onClose: () => void
}

const calculatorInputsStyles = css`
  background-color: var(--bs-gray-100);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .form-group {
    margin-bottom: 15px;
  }
  
  .text-center {
    margin-top: auto;
    margin-bottom: auto;
  }
  
  .form-label {
    font-weight: 500;
    color: ${theme.colors.secondary};
    margin-bottom: 8px;
  }
  
  .form-control {
    border: 2px solid #e9ecef;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 1rem;
    transition: border-color 0.3s;
    
    &:focus {
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 0.2rem rgba(41, 117, 186, 0.25);
    }
  }
  
  .input-group-text {
    background-color: ${theme.colors.light};
    border: 2px solid #e9ecef;
    color: ${theme.colors.secondary};
    font-weight: 500;
  }
  
  .input-group:focus-within .input-group-text {
    border-color: ${theme.colors.primary};
  }
  
  .input-group:focus-within .form-control {
    border-color: ${theme.colors.primary};
  }
`

const strategyCAGRStyles = css`
  background: var(--bs-gray-100);
  border: 2px solid ${theme.colors.primary};
  color: ${theme.colors.primary};
  border-radius: 10px;
  padding: 15px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(41, 117, 186, 0.1);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: 25px;
  }
  
  .cagr-value {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .cagr-number {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 5px;
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 1.5rem;
    }
  }
  
  .cagr-label {
    font-size: 0.9rem;
    opacity: 0.8;
    font-weight: 500;
  }
`

const resultCardStyles = css`
  background: var(--bs-gray-100);
  border: 2px solid ${theme.colors.primary};
  color: ${theme.colors.primary};
  border-radius: 10px;
  padding: 15px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(41, 117, 186, 0.1);
  
  .result-value {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 5px;
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 1.5rem;
    }
  }
  
  .result-label {
    font-size: 0.9rem;
    opacity: 0.8;
    font-weight: 500;
  }
`

const calculatorResultsStyles = css`
  min-height: 400px;
`

const navTabsStyles = css`
  .nav-link {
    color: ${theme.colors.secondary};
    border-color: transparent;
    font-weight: 500;
    
    &:hover {
      color: ${theme.colors.primary};
      border-color: ${theme.colors.primary};
    }
    
    &.active {
      color: ${theme.colors.primary};
      border-color: ${theme.colors.primary};
      background-color: transparent;
    }
    
    &.disabled {
      color: #6c757d;
      pointer-events: none;
      border-color: transparent;
      opacity: 0.5;
    }
  }
`

export const CalculatorModal = ({ isOpen, strategyName, cagr, onClose }: CalculatorModalProps) => {
  const [initialCapitalStr, setInitialCapitalStr] = useState('100000')
  const [yearsStr, setYearsStr] = useState('10')
  const [monthlyContributionStr, setMonthlyContributionStr] = useState('0')
  const [activeTab, setActiveTab] = useState('chart')

  const parseNumberValue = (value: string): number => {
    return parseFloat(value.replace(/,/g, '')) || 0
  }

  const parseIntegerValue = (value: string): number => {
    return parseInt(value.replace(/,/g, '')) || 0
  }

  const initialCapital = parseNumberValue(initialCapitalStr)
  const monthlyContribution = parseNumberValue(monthlyContributionStr)
  const years = parseIntegerValue(yearsStr)

  const { calculatorData, finalValue } = useCalculator({
    initialCapital,
    monthlyContribution,
    years,
    cagr
  })

  const hasValidData = calculatorData.length > 0

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    
    // Cleanup function to reset body overflow when component unmounts
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const formatCurrency = (value: number): string => {
    if (value >= 1000000000000) { // $1 trillion or more
      return 'Really Big'
    }
    return '$' + value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
  }

  if (!isOpen) return null

  return (
    <>
      {/* Modal backdrop */}
      <div 
        className="modal-backdrop fade show" 
        style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1040 }}
        onClick={onClose}
      ></div>
      
      <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }} tabIndex={-1}>
        <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Investment Calculator</h5>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body" style={{ padding: '2rem' }}>
            <div className="row">
              <div className="col-md-4">
                <div css={calculatorInputsStyles}>
                  <h6 className="mb-3">Investment Parameters</h6>
                  
                  <div className="form-group mb-3">
                    <label htmlFor="calcInitialCapital" className="form-label">Initial Capital</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <Cleave
                        className="form-control"
                        id="calcInitialCapital"
                        value={initialCapitalStr}
                        onChange={(e: any) => setInitialCapitalStr(e.target.value)}
                        options={{
                          numeral: true,
                          numeralThousandsGroupStyle: 'thousand',
                          numeralDecimalMark: '.',
                          delimiter: ','
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="form-group mb-3">
                    <label htmlFor="calcYears" className="form-label">Number of Years</label>
                    <Cleave
                      className="form-control"
                      id="calcYears"
                      value={yearsStr}
                      onChange={(e: any) => setYearsStr(e.target.value)}
                      options={{
                        numeral: true,
                        numeralDecimalScale: 0,
                        numeralIntegerScale: 2,
                        numeralPositiveOnly: true
                      }}
                    />
                  </div>
                  
                  <div className="form-group mb-3">
                    <label htmlFor="calcMonthlyContribution" className="form-label">Monthly Contribution</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <Cleave
                        className="form-control"
                        id="calcMonthlyContribution"
                        value={monthlyContributionStr}
                        onChange={(e: any) => setMonthlyContributionStr(e.target.value)}
                        options={{
                          numeral: true,
                          numeralThousandsGroupStyle: 'thousand',
                          numeralDecimalMark: '.',
                          delimiter: ','
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-8">
                <div css={calculatorResultsStyles}>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div css={strategyCAGRStyles}>
                        <div className="cagr-value">
                          <span className="cagr-number">{cagr}%</span>
                          <div className="cagr-label">{strategyName} CAGR</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div css={resultCardStyles}>
                        <div className="result-value">{formatCurrency(finalValue)}</div>
                        <div className="result-label">Final Account Value</div>
                      </div>
                    </div>
                  </div>
                  
                  {hasValidData && (
                    <>
                      <ul css={navTabsStyles} className="nav nav-tabs mb-3" role="tablist">
                        <li className="nav-item" role="presentation">
                          <button 
                            className={`nav-link ${activeTab === 'chart' ? 'active' : ''}`}
                            onClick={() => setActiveTab('chart')}
                            type="button"
                          >
                            <i className="fas fa-chart-line me-1"></i>View Chart
                          </button>
                        </li>
                        <li className="nav-item" role="presentation">
                          <button 
                            className={`nav-link ${activeTab === 'table' ? 'active' : ''}`}
                            onClick={() => setActiveTab('table')}
                            type="button"
                          >
                            <i className="fas fa-table me-1"></i>View Table
                          </button>
                        </li>
                      </ul>
                      
                      <div className="tab-content">
                        {activeTab === 'chart' && (
                          <div className="tab-pane fade show active">
                            <CalculatorChart data={calculatorData} />
                          </div>
                        )}
                        {activeTab === 'table' && (
                          <div className="tab-pane fade show active">
                            <CalculatorTable data={calculatorData} />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  )
}